import React from 'react';
import { motion } from 'framer-motion';
import { Card } from '@mui/material';
import AuthBackground from './AuthBackground';

// Shared MUI TextField styling for the dark glass theme
export const darkFieldSx = {
    '& .MuiOutlinedInput-root': {
        color: '#fff',
        borderRadius: '12px',
        backgroundColor: 'rgba(255,255,255,0.05)',
        '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
        '&:hover fieldset': { borderColor: 'rgba(52,199,89,0.6)' },
        '&.Mui-focused fieldset': { borderColor: '#34C759', borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#34C759' },
    '& .MuiInputBase-input': { color: '#fff' },
    '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 },
};

// Green gradient submit button styling (matches main-blue / #34C759 theme)
export const authButtonSx = {
    mt: 1,
    py: 1.4,
    borderRadius: '12px',
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #34C759 0%, #2ea34c 100%)',
    boxShadow: '0 8px 24px -6px rgba(52,199,89,0.5)',
    '&:hover': { background: 'linear-gradient(135deg, #2ea34c 0%, #279a44 100%)' },
    '&.Mui-disabled': { background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.45)' },
};

/**
 * Glassmorphism auth layout shell.
 * The container itself is solid dark so it fully covers the app's
 * light/gray wrapper; the 3D canvas + glows sit on top of that base,
 * and the glass card sits above everything. Fully responsive.
 */
export default function AuthShell({ icon, title, subtitle, children }) {
    return (
        <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden
                        px-4 py-10 sm:px-6 bg-[#060912]">
            <AuthBackground />

            {/* Dark vignette over the canvas for stronger contrast */}
            <div className="pointer-events-none absolute inset-0 z-0
                            bg-[radial-gradient(ellipse_at_center,rgba(6,9,18,0.2)_0%,rgba(6,9,18,0.85)_100%)]" />

            {/* Ambient gradient glows */}
            <div className="pointer-events-none absolute -top-32 -left-32 w-80 h-80 sm:w-96 sm:h-96 rounded-full bg-[#34C759]/25 blur-[120px] z-0" />
            <div className="pointer-events-none absolute -bottom-32 -right-32 w-80 h-80 sm:w-96 sm:h-96 rounded-full bg-[#007bff]/25 blur-[120px] z-0" />

            <motion.div
                initial={{ opacity: 0, y: 26, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="relative z-10 w-full max-w-md"
            >
                <Card
                    elevation={0}
                    sx={{
                        background: 'rgba(17, 23, 35, 0.72)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.14)',
                        borderRadius: '24px',
                        boxShadow: '0 24px 60px -12px rgba(0,0,0,0.7)',
                        p: { xs: 3, sm: 4.5 },
                    }}
                >
                    {/* Icon badge */}
                    <motion.div
                        initial={{ scale: 0, rotate: -20 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.15, type: 'spring', stiffness: 200, damping: 14 }}
                        className="mx-auto mb-5 w-16 h-16 rounded-2xl flex items-center justify-center
                                   bg-gradient-to-br from-[#34C759] to-[#2ea34c] shadow-lg shadow-[#34C759]/40 text-white"
                    >
                        {icon}
                    </motion.div>

                    <h1 className="text-2xl sm:text-[26px] font-bold text-center text-white tracking-tight">{title}</h1>
                    {subtitle && (
                        <p className="text-sm text-center text-white/65 mt-2 mb-7">{subtitle}</p>
                    )}

                    {children}
                </Card>
            </motion.div>
        </div>
    );
}
